import React, { useState } from 'react';

import { formatMoney } from '@betterment/core';
import { useGraphQLQuery } from '@betterment/graphql';
import { Button, H1, ListRow, RadioGroup, Text, VStack } from '@betterment/samba-react';
import { useNavigate } from 'react-router-dom';

import { FlowLayout } from './FlowLayout';
import { BankAccountSelectionAccountsDocument } from './graphql/query.bankAccountSelectionAccounts.generated';
import { useDistributionFlow } from './hooks';

import { AccountIcon } from '../Icon/AccountIcon';

const BankAccountSelection: React.FC = () => {
  const navigate = useNavigate();
  const { nextStep } = useDistributionFlow();
  const [selectedId, setSelectedId] = useState<string>('');

  const { data } = useGraphQLQuery(BankAccountSelectionAccountsDocument);
  const accounts = data?.externalAccounts ?? [];

  const handleContinue = () => {
    if (nextStep) void navigate(`../${nextStep}`, { relative: 'path' });
  };

  return (
    <FlowLayout gap="space.600">
      <VStack css={{ gap: 'space.500' }}>
        <VStack css={{ gap: 'space.200' }}>
          <H1 variant="title400">Select an account.</H1>
          <Text variant="text100">The following connected accounts are eligible funding sources.</Text>
        </VStack>

        <RadioGroup appearance="default" value={selectedId || undefined} onValueChange={setSelectedId}>
          <>
            {accounts.map(account => (
              <RadioGroup.ItemContainer key={account.id}>
                <RadioGroup.Item id={account.id} value={account.id} aria-label={account.name} />
                <RadioGroup.ItemLabel htmlFor={account.id}>
                  <ListRow css={{ padding: 0 }}>
                    <ListRow.LeadingAsset css={{ width: '36px', height: '36px' }}>
                      <AccountIcon asset={account.icon} name={account.name} />
                    </ListRow.LeadingAsset>
                    <ListRow.LeadingContent>
                      <Text variant="text100">{account.name}</Text>
                      <Text variant="text50" color="text.weak">
                        {account.accountTypeAndRedactedAccountNumber} – {formatMoney(account.balance)}
                      </Text>
                    </ListRow.LeadingContent>
                  </ListRow>
                </RadioGroup.ItemLabel>
              </RadioGroup.ItemContainer>
            ))}

            <RadioGroup.ItemContainer>
              <RadioGroup.Item id="connect_new" value="connect_new" aria-label="Connect a new institution" />
              <RadioGroup.ItemLabel htmlFor="connect_new">
                <Text variant="text100">Connect a new institution</Text>
              </RadioGroup.ItemLabel>
            </RadioGroup.ItemContainer>
          </>
        </RadioGroup>
      </VStack>

      <Button
        variant="solidPrimary"
        css={{ width: 'fit-content' }}
        disabled={!selectedId}
        onClick={() => void handleContinue()}
      >
        Continue
      </Button>
    </FlowLayout>
  );
};

export default BankAccountSelection;
